import {
	BUDGET_TYPE,
	type BudgetType,
	HIDDEN_FEATURES,
	MODULE_TYPES,
	PERIOD_BUDGET_TYPE,
	type PeriodBudgetType,
} from '../../../constants';
import {PERMISSION_TYPE} from '../../../Permissions';
import CompanySetupUtil, {isFeatureHidden} from './CompanySetupUtil';
import {hasFeatureFlag} from './FeatureUtil';
import {hasModule} from './ModuleUtil';
import {hasSomePermission} from './PermissionsUtil';
import {normalizeForSearch} from './StringUtil';
import {Connection, ConnectionEdge} from './type-util/Connection';

export type ProjectNode = Partial<{
	totalBillableExpensesAtCompletion: number | null;
	totalTimeRevenueRecognitionToDate: number | null;
	totalRevenueRecognition: number | null;
	manualProgressOnProjectEnabled: boolean | null;
	manualProgressOnPhasesEnabled: boolean | null;
	manualProgressOnTasksEnabled: boolean | null;
	useBaseline: boolean | null;
	useDeliverables: boolean | null;
	budgetType: string | null;
	defaultPeriodBudgetType: PeriodBudgetType;
}>;
type ProjectEdge = ConnectionEdge<ProjectNode>;

type Task = {
	totalCostAtCompletion: number | null;
	estimateForecastPrice: number | null;
	allTotalTimeAndExpensesAtCompletion: number | null;
	totalPriceAtCompletion: number | null;
	actualCost: number | null;
	plannedCost: number | null;
	currentPrice: number;
	approved: unknown;
	timeRegistrations: Connection<{price: number}, null> | null;
	billable: unknown;
};

/**
 * True when `character` is a letter or digit in any script. An ASCII-only test would treat every
 * accented or non-Latin letter as a word boundary, so a two-letter card name would still be pulled
 * out of the middle of a word like "läse" - exactly what containsAsWords exists to prevent.
 */
const isWordChar = (character: string): boolean => /[\p{L}\p{N}]/u.test(character);

/**
 * True when `needle` appears inside `haystack` delimited by something other than a letter or
 * digit on both sides. Plain `includes` would let a two-letter card name match the middle of an
 * unrelated word, which is how a free-text reference ends up resolving to the wrong card.
 */
const containsAsWords = (haystack: string, needle: string): boolean => {
	if (!needle) {
		return false;
	}
	for (let index = haystack.indexOf(needle); index !== -1; index = haystack.indexOf(needle, index + 1)) {
		const before = index === 0 ? '' : haystack.charAt(index - 1);
		const after = haystack.charAt(index + needle.length);
		if (!isWordChar(before) && !isWordChar(after)) {
			return true;
		}
	}
	return false;
};

export default class ProjectUtil {
	static handleSprintLengthChange(value: string): string | number {
		// Clamp sprint length between 0 and 365
		const clampedValue = Math.max(0, Math.min(365, parseInt(value, 10)));
		return isNaN(clampedValue) ? value : clampedValue;
	}

	static getDefaultRateCard(
		formatMessage: (descriptor: unknown, values?: unknown) => string,
		defaultRateCard: {id: unknown; name: unknown; currency: unknown} | undefined,
		companyCurrency: unknown
	): {value: unknown; label: unknown; currency: unknown} {
		if (defaultRateCard) {
			return {
				value: defaultRateCard.id,
				label: defaultRateCard.name,
				currency: defaultRateCard ? defaultRateCard.currency : companyCurrency,
			};
		}

		const label = hasFeatureFlag('sage_intacct_requested_currencies_for_forecast')
			? formatMessage({id: 'rate_card_selector.using_company_base_currency'}, {code: companyCurrency})
			: formatMessage({id: 'rate_card_selector.no_rate_card_text'});

		return {value: null, label, currency: companyCurrency};
	}

	/**
	 * True when a rate card can be picked for a project: parent (not adjusted) cards that are still
	 * enabled. This is the single definition of "offered by the rate card dropdown" - anything that
	 * pre-selects a card must agree with it, or the modal opens on a card the dropdown never lists.
	 */
	static isSelectableRateCard(rateCard: {parentRateCardId?: unknown; disabled?: unknown} | null | undefined): boolean {
		return !!rateCard && !rateCard.parentRateCardId && !rateCard.disabled;
	}

	/**
	 * Find the one rate card a free-text reference names, or null when it names none or several.
	 *
	 * The reference comes from what a user said to the assistant, so "premium" has to find
	 * "Premium Rate Card" - an exact-name rule only works when they recite the whole name.
	 * Matching widens in stages and stops at the first stage naming exactly one card, so an
	 * ambiguous "premium" across two premium cards resolves to nothing rather than a guess.
	 * A card name only counts as appearing inside a longer phrase when it sits on word
	 * boundaries, so a card called "US" is not pulled out of the middle of "use the ...".
	 * Callers decide what "nothing" means; the create-project modal falls back to the company
	 * default.
	 */
	static matchRateCardByReference<T extends {id: string; name: string}>(cards: T[], reference: string): T | null {
		const trimmed = typeof reference === 'string' ? reference.trim() : '';
		const wanted = normalizeForSearch(trimmed);
		if (!wanted) {
			return null;
		}

		type Candidate = {card: T; name: string};
		// Fold each name once rather than per stage. normalizeForSearch is what the admin finance page's
		// rate card search already folds with, so a user who says "radgivning" finds "Rådgivning" in both
		// places. Without it every stage compares an accented name against an unaccented reference and
		// fails, and the modal silently opens on the company default instead of the card the user named.
		const candidates: Candidate[] = cards.map(card => ({card, name: normalizeForSearch((card.name || '').trim())}));

		const stages: Array<(candidate: Candidate) => boolean> = [
			// An id the assistant already knows, handed straight back.
			({card}) => card.id === trimmed,
			({name}) => name === wanted,
			({name}) => !!name && name.startsWith(wanted),
			({name}) => !!name && (containsAsWords(name, wanted) || containsAsWords(wanted, name)),
		];

		for (const matches of stages) {
			const hits = candidates.filter(matches);
			if (hits.length === 1) {
				return hits[0].card;
			}
		}
		return null;
	}

	/**
	 * True when the create-project modal actually shows its rate card dropdown for `budgetType`.
	 *
	 * Opening the "more settings" section is not enough. The dropdown sits behind four further gates -
	 * two financial permissions, the budget-types hidden feature, a non-billable budget type, and the
	 * finance module - while the modal sends `rateCardSelected` on create either way. Anything that
	 * pre-selects a card has to ask this first, or it applies a card the user was never shown and only
	 * finds out about when the project is later switched to time and materials.
	 *
	 * Mirrors new_project_modal_detailed_settings (the permission and hidden-feature gates) and
	 * project_budget_type_controls (the budget type and finance module gates).
	 */
	static isRateCardPickerVisible(budgetType: BudgetType | string | null | undefined): boolean {
		return (
			hasSomePermission([
				PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION,
				PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
			]) &&
			!isFeatureHidden(HIDDEN_FEATURES.BUDGET_TYPES) &&
			budgetType !== BUDGET_TYPE.NON_BILLABLE &&
			CompanySetupUtil.hasFinance()
		);
	}

	/**
	 * The rate card option the create-project modal opens on.
	 *
	 * The chat agent supplies a card by the name the user said, or asks for none at all. Anything that
	 * does not resolve to exactly one selectable card falls back to the company default, which is what
	 * a plain open of this modal has always shown - "premium" across two premium cards is not a licence
	 * to pick one.
	 *
	 * A pre-selection is only honoured when the picker is visible for `budgetType`. Where it is hidden
	 * the user cannot see or correct the choice, so the agent's card is dropped and the default stands.
	 *
	 * The returned object has to be identity-comparable against the dropdown's own options, so both
	 * the candidate filter and the option shape come from the same place the dropdown builds them:
	 * isSelectableRateCard and {value, label, currency}.
	 */
	static resolveInitialRateCard<
		T extends {id: string; name: string | null; currency?: unknown; parentRateCardId?: unknown; disabled?: unknown}
	>(options: {
		rateCards: Array<T | null | undefined>;
		budgetType: BudgetType | string | null | undefined;
		initialRateCardName?: string | null;
		initialNoRateCard?: boolean;
		defaultRateCard: {id: unknown; name: unknown; currency: unknown} | undefined;
		companyCurrency: unknown;
		formatMessage: (descriptor: unknown, values?: unknown) => string;
	}): {value: unknown; label: unknown; currency: unknown} {
		const {rateCards, budgetType, initialRateCardName, initialNoRateCard, defaultRateCard, companyCurrency, formatMessage} =
			options;

		const companyDefault = ProjectUtil.getDefaultRateCard(formatMessage, defaultRateCard, companyCurrency);

		if (!ProjectUtil.isRateCardPickerVisible(budgetType)) {
			return companyDefault;
		}
		// "No rate card" is the dropdown's own first option: getDefaultRateCard with no default card
		// produces exactly it, label included.
		if (initialNoRateCard) {
			return ProjectUtil.getDefaultRateCard(formatMessage, undefined, companyCurrency);
		}
		if (!initialRateCardName) {
			return companyDefault;
		}

		// Names are nullable on the connection, and a card the dropdown does not offer must not be
		// pre-selected, so drop both before matching.
		const candidates = rateCards.filter(
			(card): card is T & {name: string} => !!card && !!card.name && ProjectUtil.isSelectableRateCard(card)
		);
		const match = ProjectUtil.matchRateCardByReference(candidates, initialRateCardName);
		return match ? {value: match.id, label: match.name, currency: match.currency} : companyDefault;
	}

	static projectHasManualProgressSettingEnabled(project: ProjectNode | null): boolean | null | undefined {
		return (
			project &&
			(project.manualProgressOnProjectEnabled ||
				project.manualProgressOnPhasesEnabled ||
				project.manualProgressOnTasksEnabled)
		);
	}

	static projectUsesManualProgress(project: ProjectEdge | ProjectNode | null): boolean | null | undefined {
		const projectEntity = (project as ProjectEdge | null)?.node || (project as ProjectNode | null);
		return (
			(isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) && !hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) ||
			this.projectHasManualProgressSettingEnabled(projectEntity)
		);
	}

	static projectTracksTime(project: ProjectEdge | ProjectNode | null): boolean | null {
		const projectEntity = (project as ProjectEdge | null)?.node || (project as ProjectNode | null);
		return (
			!isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) && !this.projectHasManualProgressSettingEnabled(projectEntity)
		);
	}

	static projectTracksRevenue(project: Pick<ProjectNode, 'budgetType'> | null): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.REVENUE) && project?.budgetType !== BUDGET_TYPE.NON_BILLABLE;
	}

	static projectTracksCost(): boolean {
		return CompanySetupUtil.hasCost();
	}

	static projectHasTaskBreakdown(budgetType: BudgetType): boolean {
		if (hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && !isFeatureHidden(HIDDEN_FEATURES.TASK_BREAKDOWN)) {
			return true;
		}

		return this.projectHasRevenueRecognition(budgetType);
	}

	static projectHasRevenueRecognition(budgetType: BudgetType): boolean {
		return CompanySetupUtil.hasRevenueRecognition() && ProjectUtil.projectTracksRevenue({budgetType});
	}

	static projectHasExpenses(budgetType: BudgetType): boolean {
		return ProjectUtil.projectTracksRevenue({budgetType}) && CompanySetupUtil.hasExpenses();
	}

	static projectHasInvoicing(budgetType: BudgetType): boolean {
		return ProjectUtil.projectTracksRevenue({budgetType}) && CompanySetupUtil.hasFinance();
	}

	static isFixedPriceV1(project: ProjectNode): boolean {
		return ProjectUtil.projectTracksRevenue(project) && project.budgetType === BUDGET_TYPE.FIXED_PRICE;
	}

	static isFixedPriceV2(project: ProjectNode): boolean {
		return ProjectUtil.projectTracksRevenue(project) && project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	}

	static isAnyKindOfFixedPrice(project: ProjectNode): boolean {
		return (
			ProjectUtil.projectTracksRevenue(project) &&
			(project.budgetType === BUDGET_TYPE.FIXED_PRICE ||
				project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
				(project.budgetType === BUDGET_TYPE.RETAINER &&
					project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE))
		);
	}

	static isFixedPriceRetainer(project: ProjectNode | null | undefined): boolean {
		return (
			project?.budgetType === BUDGET_TYPE.RETAINER && project?.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE
		);
	}

	static allProjectsUseManualProgress = (projects: ProjectNode[] | ProjectEdge[] | null): boolean => {
		if (isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
			return true;
		}
		if (!projects || !Array.isArray(projects)) {
			return false;
		}
		return (projects as Array<ProjectNode | ProjectEdge>).every((project: ProjectNode | ProjectEdge) =>
			ProjectUtil.projectUsesManualProgress(project)
		);
	};

	static allProjectEdgesUseManualProgress = (edges: ProjectEdge[] | null): boolean => {
		if (isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
			return true;
		}
		return edges ? ProjectUtil.allProjectsUseManualProgress(edges.map(edge => edge.node)) : false;
	};

	/**
	 * returns correct actual revenue based on billable
	 * @param task task
	 * - taskCount: amount of tasks in project
	 * - billableScope: combined scope of all billable + approved tasks in minutes
	 * - newFixedPrice: project fixed revenue if has featureflag and fixed revenue project, undefined otherwise
	 * @returns actual revenue for task
	 */
	static getActualPriceForTask(task: Task): number {
		return task.currentPrice;
	}

	static getPlannedCostForTask(task: Task): number {
		return task.plannedCost || 0;
	}

	static getActualCostForTask(task: Task): number {
		return task.actualCost || 0;
	}

	static getTotalPriceAtCompletionForTask(task: Task): number {
		return task.totalPriceAtCompletion || 0;
	}

	static getTotalAllTotalTimeAndExpensesAtCompletionForTask(task: Task): number {
		return task.allTotalTimeAndExpensesAtCompletion || 0;
	}

	/**
	 * returns correct Planned Revenue based on billable
	 * @param task task
	 * - taskCount: amount of tasks in project
	 * - billableScope: combined scope of all billable + approved tasks in minutes
	 * - newFixedPrice: project fixed revenue if has featureflag and fixed revenue project, undefined otherwise
	 * @returns Planned Revenue for task
	 */
	static getEstimatedPrice(task: Task): number | null {
		return task.estimateForecastPrice;
	}

	static isFixedPriceProject(project: ProjectNode | null): boolean {
		return (
			project?.budgetType === BUDGET_TYPE.FIXED_PRICE ||
			project?.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
			(project?.budgetType === BUDGET_TYPE.RETAINER &&
				project?.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE)
		);
	}

	static getProjectConfirmedRevenue(project: ProjectNode): number {
		return ProjectUtil.isFixedPriceProject(project)
			? project.totalRevenueRecognition || 0
			: (project.totalTimeRevenueRecognitionToDate || 0) + (project.totalBillableExpensesAtCompletion || 0);
	}

	static getProjectConfirmedRevenueTotal = (projects: ProjectNode[] | null): number => {
		if (!projects || !projects.reduce) {
			return 0;
		}
		return projects.reduce((total, project) => {
			const projectConfirmedRevenue = ProjectUtil.getProjectConfirmedRevenue(project);
			return total + projectConfirmedRevenue;
		}, 0);
	};

	static getProjectedBillableValueOfServiceForTask(task: Task): number | null {
		return task.totalPriceAtCompletion;
	}

	static getProjectedCostForTask(task: Task): number | null {
		return task.totalCostAtCompletion;
	}

	static projectCompatibleWithDeliverables(project: ProjectNode): boolean | null | undefined {
		const compatibleTypes: string[] = [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2, BUDGET_TYPE.TIME_AND_MATERIALS];
		return !!(project.budgetType && compatibleTypes.includes(project.budgetType));
	}

	static deliverablesUiEnabled(project: ProjectNode | null | undefined): boolean {
		return !!(
			hasFeatureFlag('deliverables') &&
			project?.useDeliverables &&
			ProjectUtil.projectCompatibleWithDeliverables(project)
		);
	}

	static sortPhasesBySortOrder = <T extends {readonly node: {readonly sortOrder?: number | null} | null} | null | undefined>(
		phaseEdges: readonly (T | null | undefined)[]
	): T[] => {
		// Check if any phases have a sortOrder
		const hasAnySortOrder = phaseEdges.some(edge => edge?.node?.sortOrder != null);

		// If no phases have sortOrder, return the array as is
		if (!hasAnySortOrder) {
			return [...phaseEdges] as T[];
		}

		// Sort phases by sortOrder. Phases without sortOrder are placed at the end.
		return [...phaseEdges].sort((a, b) => {
			const aSortOrder = a?.node?.sortOrder ?? Number.MAX_SAFE_INTEGER;
			const bSortOrder = b?.node?.sortOrder ?? Number.MAX_SAFE_INTEGER;
			return aSortOrder - bSortOrder;
		}) as T[];
	};
}
